import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MailX, Check, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import api from '../services/api';

/**
 * Public Unsubscribe Page
 * Allows customers to manage their email preferences via a token link
 * 
 * URL: /unsubscribe?token={token}
 */
const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [info, setInfo] = useState(null);

  // Preference toggles
  const [preferences, setPreferences] = useState({
    unsubscribeAll: false,
    marketing: false,
    reminders: false,
    reviews: false,
  });

  useEffect(() => {
    if (token) {
      loadUnsubscribeInfo();
    } else {
      setError('Invalid unsubscribe link. Please use the link from your email.');
      setLoading(false);
    }
  }, [token]);

  const loadUnsubscribeInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/public/unsubscribe?token=${token}`);
      setInfo(response.data);
      
      // Set current preferences
      setPreferences({
        unsubscribeAll: response.data.unsubscribedAll,
        marketing: response.data.unsubscribedMarketing,
        reminders: response.data.unsubscribedReminders,
        reviews: response.data.unsubscribedReviews,
      });
    } catch (err) {
      console.error('Error loading unsubscribe info:', err);
      setError(err.response?.data?.error || 'Invalid or expired unsubscribe link.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await api.post('/public/unsubscribe', {
        token,
        unsubscribeAll: true,
      });
      
      setSuccess(response.data.message);
      setPreferences({
        unsubscribeAll: true,
        marketing: true,
        reminders: true,
        reviews: true,
      });
    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError(err.response?.data?.error || 'Failed to update preferences. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePreferences = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await api.post('/public/unsubscribe', {
        token,
        unsubscribeAll: false,
        unsubscribeMarketing: preferences.marketing,
        unsubscribeReminders: preferences.reminders,
        unsubscribeReviews: preferences.reviews,
      });
      
      setSuccess(response.data.message);
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError(err.response?.data?.error || 'Failed to update preferences. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubscribe = async () => {
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await api.post('/public/resubscribe', { token });
      
      setSuccess(response.data.message);
      setPreferences({
        unsubscribeAll: false,
        marketing: false,
        reminders: false,
        reviews: false,
      });
    } catch (err) {
      console.error('Error resubscribing:', err);
      setError(err.response?.data?.error || 'Failed to resubscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid token)
  if (error && !info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const brandColor = info?.brandColor || '#3B82F6';
  const isFullyUnsubscribed = preferences.unsubscribeAll;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header with Business Branding */}
        <div className="text-center mb-8">
          {info?.businessLogo ? (
            <img 
              src={info.businessLogo} 
              alt={info.businessName}
              className="h-36 sm:h-40 md:h-48 mx-auto mb-4 object-contain"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${brandColor}20` }}
            >
              <Mail className="w-8 h-8" style={{ color: brandColor }} />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">Email Preferences</h1>
          <p className="text-gray-600 mt-1">{info?.businessName}</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Customer Info Header */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <p className="text-sm text-gray-600">
              Managing preferences for: <span className="font-medium text-gray-900">{info?.customerEmail}</span>
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div className="mx-6 mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-green-800 font-medium">{success}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && info && (
            <div className="mx-6 mt-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          )}

          <div className="p-6">
            {/* Already Unsubscribed State */}
            {isFullyUnsubscribed && !success && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MailX className="w-8 h-8 text-gray-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 mb-2">
                  You're Unsubscribed
                </h2>
                <p className="text-gray-600 mb-6">
                  You won't receive any automated emails from {info?.businessName}.
                </p>
                <button
                  onClick={handleResubscribe}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Resubscribe to Emails
                </button>
              </div>
            )}

            {/* Preference Options */}
            {!isFullyUnsubscribed && (
              <>
                {/* Quick Unsubscribe */}
                <div className="mb-6">
                  <button
                    onClick={handleUnsubscribeAll}
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-700 font-medium rounded-xl transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <MailX className="w-5 h-5" />
                    )}
                    Unsubscribe from All Emails
                  </button>
                </div>

                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-white text-sm text-gray-500">or manage by category</span>
                  </div>
                </div>

                {/* Category Preferences */}
                <div className="space-y-4">
                  <PreferenceToggle
                    label="Marketing & Promotions"
                    description="Special offers, seasonal promotions, and newsletters"
                    checked={preferences.marketing}
                    onChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                    brandColor={brandColor}
                  />

                  <PreferenceToggle
                    label="Appointment Reminders"
                    description="Service prep instructions and appointment reminders"
                    checked={preferences.reminders}
                    onChange={(checked) => setPreferences({ ...preferences, reminders: checked })}
                    brandColor={brandColor}
                  />

                  <PreferenceToggle
                    label="Review Requests"
                    description="Requests for feedback after service completion"
                    checked={preferences.reviews}
                    onChange={(checked) => setPreferences({ ...preferences, reviews: checked })}
                    brandColor={brandColor}
                  />
                </div>

                {/* Save Preferences Button */}
                <button
                  onClick={handleUpdatePreferences}
                  disabled={submitting}
                  className="w-full mt-6 px-6 py-3 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
                  style={{ backgroundColor: brandColor }}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Preferences'
                  )}
                </button>
              </>
            )}
          </div>

          {/* Footer Note */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-xs text-gray-500 text-center">
              <strong>Note:</strong> You will still receive transactional emails such as invoices, 
              payment confirmations, and appointment confirmations regardless of these settings.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Powered by <span className="font-medium text-gray-700">SuddyFlow</span></p>
        </div>
      </div>
    </div>
  );
};

/**
 * Preference Toggle Component
 * Shows as "unsubscribed" when checked (red), "subscribed" when unchecked (green)
 */
const PreferenceToggle = ({ label, description, checked, onChange, brandColor }) => {
  return (
    <label className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
      <div className="flex-1">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-sm text-gray-600 mt-0.5">{description}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`text-xs font-medium ${checked ? 'text-red-600' : 'text-green-600'}`}>
          {checked ? 'Unsubscribed' : 'Subscribed'}
        </span>
        <button
          type="button"
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            checked ? 'bg-red-500' : 'bg-green-500'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </label>
  );
};

export default UnsubscribePage;